import { useCallback } from 'react';

import { ApiScan, createUnapprovalNote, ScanNoteType } from '@kaiizen/kai-pro-api';
import { type ScanMutators, useScanEditingContextSlice, useScanNotesMutators } from '@kaiizen/kai-pro-api-react-state';

import { ANALYTICS_EVENTS, trackEvent } from '../analytics';

/**
 * A scan sitting in `pendingReview` has been submitted but nobody has acted on
 * it yet. Filing the first review note is that act, so it moves the scan to
 * `inReview` — every caller of this write path did this itself before.
 */
const startReviewingScan = async (scan: ApiScan, scanMutators: ScanMutators): Promise<void> => {
  if (scan.scanLifecycleStatus !== 'pendingReview') return;
  await scanMutators.update(scan.id, { scanLifecycleStatus: 'inReview' });
};

const trackApprovedTasks = (scanId: string, taskCount: number) =>
  trackEvent(ANALYTICS_EVENTS.scanApproved, {
    scanId,
    tasksApproved: taskCount,
    bulkApproval: taskCount > 1,
  });

/**
 * The one write path for scope review: files a single bare note per task, which
 * is how approval and edit-request state is stored (there is no field on scan
 * data for it — see `isScanPartApproved`).
 *
 * Returns the action rather than an object holding it, because the action is the
 * whole point: `const createReviewNotes = useCreateReviewNotes()`.
 *
 * Takes ids rather than tasks so one call covers every grain the UI approves at
 * — one task from a row, a group's worth from a group header, the whole scope
 * from the scope header. Duplicate ids are collapsed and an empty list is a
 * no-op, so callers can hand over whatever their selection produced.
 */
export const useCreateReviewNotes = () => {
  const { scan, scanMutators } = useScanEditingContextSlice(['scan', 'scanMutators']);
  const scanNoteMutators = useScanNotesMutators(scan?.id);

  return useCallback(async (taskIds: readonly string[], noteType: ScanNoteType): Promise<void> => {
    if (!scan) return;
    const uniqueTaskIds = [...new Set(taskIds.filter(Boolean))];
    if (!uniqueTaskIds.length) return;

    await startReviewingScan(scan, scanMutators);
    for (const scanPartId of uniqueTaskIds) {
      await scanNoteMutators.create({ scanPartId, scanNoteType: noteType, body: '' });
    }
    if (noteType === 'approval') trackApprovedTasks(scan.id, uniqueTaskIds.length);
  }, [scan, scanMutators, scanNoteMutators]);
};

export const useCreateUnapprovalNote = () => {
  const { scanId, scanNotesByScanPartId } = useScanEditingContextSlice(['scanId', 'scanNotesByScanPartId']);
  const scanNoteMutators = useScanNotesMutators(scanId);

  return useCallback(async (taskIds: readonly string[]): Promise<void> => {
    if (!scanId) return;
    const uniqueTaskIds = [...new Set(taskIds.filter(Boolean))];
    if (!uniqueTaskIds.length) return;

    for (const scanPartId of uniqueTaskIds) {
      const unapprovalNote = createUnapprovalNote(scanId, scanPartId, scanNotesByScanPartId);
      if (unapprovalNote) {
        await scanNoteMutators.create(unapprovalNote);
      }
    }
  }, [scanId, scanNoteMutators, scanNotesByScanPartId]);
};
